import React from 'react';
import { CustomColorPicker } from 'editor/editors/highed.init';
import SelectWidget from '../SelectWidget';
import { Quill } from 'react-quill';
import { colourStyles, richtextStyles } from 'shared/editor/PublishHelper';
import FontPickerWidget from './../FontPickerWidget';
import { get, isArray, isObject } from 'lodash';
import IconButton from 'shared/buttons/IconButton';
import { getValue } from '../helpers/TooltipHelper';
import { customSelectColors } from 'shared/inputs/CustomSelect';

const mapping = {
  bold: {
    key: 'fontWeight',
    value: 'bold'
  },
  underline: {
    key: 'textDecoration',
    value: 'underline'
  },
  italic: {
    key: 'fontStyle',
    value: 'italic'
  }
};
export const fontSizes = [
  {
    value: '9px',
    label: 9
  },
  {
    value: '10px',
    label: 10
  },
  {
    value: '12px',
    label: 12
  },
  {
    value: '14px',
    label: 14
  },
  {
    value: '16px',
    label: 16
  },
  {
    value: '18px',
    label: 18
  },
  {
    value: '20px',
    label: 20
  },
  {
    value: '22px',
    label: 22
  },
  {
    value: '24px',
    label: 24
  },
  {
    value: '26px',
    label: 26
  },
  {
    value: '28px',
    label: 28
  },
  {
    value: '30px',
    label: 30
  },
  {
    value: '32px',
    label: 32
  }
];
export const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'color',
  'link',
  'image',
  'align',
  'script',
  'size'
];

let fontSizeStyle = Quill.import('attributors/style/size');
fontSizeStyle.whitelist = fontSizes.map((c) => c.value);
Quill.register(fontSizeStyle, true);

const extractTableValue = (options, chart, aggregatedOptions) => {
  return {
    ...(get(aggregatedOptions, options.id.replace('.text', '')) ?? {})
  };
};

const extractChartValue = (options, chart) => {
  let index = null;
  if (options.richtext?.isArray) index = 0;

  const isPropArray = isArray(options.inline?.highchartsId);
  // if (options.default) settings = options.default;
  if (isPropArray) {
    // For style/title that are in different places. (credits)
    // index 0 = where style can be found in the chart object.
    // index 1 = where text can be found in the chart object.
    return {
      style: {
        ...get(chart, options.inline?.highchartsId[0], index)
      },
      text: get(chart, options.inline?.highchartsId[1], index)
    };
  }
  return { ...get(chart, options.inline?.highchartsId, index) };
};

const extractValueMap = {
  table: extractTableValue,
  layout: extractTableValue,
  chart: extractChartValue
};

const extractSectionValueMap = {
  tooltip: getValue
};

const tableMapping = {
  align: 'textAlign'
};

const alignValues = [
  { value: 'left', icon: 'align-left' },
  { value: 'center', icon: 'align-center' },
  { value: 'right', icon: 'align-right' }
];
const axisAlignValues = [
  { value: 'low', icon: 'align-left' },
  { value: 'middle', icon: 'align-center' },
  { value: 'high', icon: 'align-right' }
];

export const extractValue = (type, options, chart, aggregatedOptions) => {
  let settings = {
    text: '',
    style: {}
  };

  if (!options) return settings;

  settings = extractValueMap[type](options, chart, aggregatedOptions);
  settings = settings ?? {};

  const extractText = extractSectionValueMap[options.richtext?.rawId ?? ''];

  if (extractText) settings.text = extractText(options.richtext.type, chart, aggregatedOptions);
  if (!settings.style) settings.style = {};

  delete settings.style.width;
  delete settings.style.height;

  return settings ?? {};
};

export const generateToolbarSettings = ({
  type,
  settings,
  id,
  options,
  ref,
  onChange,
  inlineSettings,
  setInlineSettings,
  aggregatedOptions,
  fields,
  className,
  noHighchartOptions
}) => {
  const isChart = type === 'chart';
  const useAxisTitleAlignValue = settings?.id?.includes('Axis[0].title.text');
  const alignArray = useAxisTitleAlignValue ? axisAlignValues : alignValues;

  const editHighchartsProp = (key, value, suffix, isStyle = true, clearProp = true) => {
    const editor = ref.current.getEditor();
    key = isChart ? key : tableMapping[key] ?? key;
    let newValue = value;
    if (isStyle) {
      if (options?.style?.[key] && clearProp) newValue = null;
      key = `style.${key}`;
    }

    onChange(key, newValue, settings.isOptionalArray);

    if (!options?.style?.[key]) {
      editor.setSelection(0, editor.getText().replace('\n', '').length);
      editor.format(suffix, false, 'user');
    }
  };

  const editInlineProp = (key, value, suffix, isDynamic) => {
    const editor = ref.current.getEditor();
    const hasHighchartsStyling = options?.style?.[key];
    onChange(`style.${key}`, null);
    let selection = editor.getSelection();
    let effectStatus = !editor.getFormat(selection)[suffix];

    if (hasHighchartsStyling) {
      editor.setSelection(0, editor.getText().replace('\n', '').length);
      editor.format(suffix, hasHighchartsStyling, 'user');
      editor.setSelection(selection.index, selection.length);
      effectStatus = false;
    }

    if (isDynamic) editor.format(suffix, value, 'user');
    else {
      editor.format(suffix, effectStatus, 'user');
    }
  };

  const handleAlignmentChange = (key, value, suffix) => {
    const adjustValueForQuill = value === 'left' ? '' : value;
    if (settings.id === 'tooltip') editInlineProp(key, adjustValueForQuill, suffix, true);
    else editHighchartsProp(key, value, suffix, !isChart, false);
  };

  const onClickOption = (overrideOption, changeValue, clearProp) => {
    const editor = ref.current.getEditor();
    let selection = editor.getSelection();
    const allText = editor.getText().replace('\n', '');
    let mappedValue, suffix;

    if (isObject(overrideOption)) {
      mappedValue = overrideOption;
      suffix = overrideOption.suffix;
    } else {
      suffix = overrideOption;
      mappedValue = mapping[suffix];
    }

    if (!noHighchartOptions && (!selection || selection.length === allText.length || selection?.length === 0)) {
      // Selected all text, edit highcharts prop instead so it can be used in a theme
      editHighchartsProp(mappedValue.key, mappedValue.value, suffix, true, clearProp ?? true);
    } else {
      // Only selected partial text, edit inline and clear the highcharts prop
      if ((!noHighchartOptions && !selection) || selection?.length === 0) {
        editor.setSelection(0, editor.getText().replace('\n', '').length);
      }
      editInlineProp(mappedValue.key, mappedValue.value, suffix, changeValue);
      return false;
    }
  };

  const onClickColor = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const editor = ref.current.getEditor();
    let selection = editor.getSelection();

    // eslint-disable-next-line no-new
    new CustomColorPicker(e.clientX, e.clientY, '', (col) => {
      if (selection) editor.setSelection(selection.index, selection.length);
      const allText = editor.getText().replace('\n', '');
      if (!noHighchartOptions && (!selection || selection.length === allText.length || selection?.length === 0)) {
        // Selected all text, edit highcharts prop instead so it can be used in a theme
        editHighchartsProp('color', col, 'color', true, false);
      } else {
        // Only selected partial text, edit inline and clear the highcharts prop
        editInlineProp('color', col, 'color', true, false);
      }
      setInlineSettings({
        ...inlineSettings,
        color: col
      });
    });

    return false;
  };

  // eslint-disable-next-line func-style
  const onSizeChange = function (value) {
    if (value) {
      const editor = ref.current.getEditor();
      let selection = editor.getSelection() ?? editor.selection.savedRange;
      editor.setSelection(selection.index, selection.length);
      onClickOption(
        {
          key: 'fontSize',
          value: value.val,
          suffix: 'size'
        },
        true,
        false
      );
    }
  };

  const onChangeFont = (value) => editHighchartsProp('fontFamily', value.val, 'fontFamily', true, false);

  const toolbarOptions = {
    toolbar: {
      container: `#${id}`
    }
  };

  const toolbar = (
    <div className={`ql-toolbar ql-snow border-none pb-0 ${className}`} id={`${id}`}>
      <div className="gap-y-2 flex flex-wrap mb-2">
        <div className="flex space-x-1 items-center">
          {fields.includes('bold') && (
            <IconButton
              icon={'bold'}
              onClick={() => onClickOption('bold')}
              className="rounded h-8 w-8"
              active={inlineSettings.fontWeight || options?.style?.fontWeight === 'bold'}
            />
          )}

          {fields.includes('underline') && (
            <IconButton
              icon={'underline'}
              onClick={() => onClickOption('underline')}
              className="rounded h-8 w-8"
              active={inlineSettings.textDecoration || options?.style?.textDecoration === 'underline'}
            />
          )}

          {fields.includes('italic') && (
            <IconButton
              icon={'italic'}
              onClick={() => onClickOption('italic')}
              className="rounded h-8 w-8"
              active={inlineSettings.fontStyle || options?.style?.fontStyle === 'italic'}
            />
          )}

          {fields.includes('fontSize') && (
            <SelectWidget
              options={{}}
              selectOptions={fontSizes}
              onChange={onSizeChange}
              customStyles={{ ...richtextStyles }}
              value={(inlineSettings.fontSize || options?.style?.fontSize) ?? '9px'}
              inlineSelect={true}
            />
          )}

          {fields.includes('align') && (
            <span className="ql-formats alignment flex space-x-1">
              <IconButton
                icon={alignArray[0].icon}
                onClick={() => handleAlignmentChange('align', alignArray[0].value, 'align')}
                className="rounded h-8 w-8"
                active={options?.align === alignArray[0].value}
              />
              <IconButton
                icon={alignArray[1].icon}
                onClick={() => handleAlignmentChange('align', alignArray[1].value, 'align')}
                className="rounded h-8 w-8"
                active={options?.align === alignArray[1].value}
              />
              <IconButton
                icon={alignArray[2].icon}
                onClick={() => handleAlignmentChange('align', alignArray[2].value, 'align')}
                className="rounded h-8 w-8"
                active={options?.align === alignArray[2].value}
              />
            </span>
          )}

          {fields.includes('color') && (
            <span>
              <button
                onClick={onClickColor}
                className="color-picker-widget border-4 border-solid border-white drop-shadow-color-widget-shadow w-[30px] h-[30px]"
                style={{
                  backgroundColor: (inlineSettings.color || options?.style?.color) ?? 'black'
                }}
              />
            </span>
          )}
        </div>
        {fields.includes('font') && (
          <div className="w-full">
            <FontPickerWidget
              option={{
                ...(settings ?? {}),
                custom: {
                  simple: true
                },
                id: `${settings?.richtext?.rawId ?? 'title'}.style`
              }}
              customStyles={colourStyles}
              aggregatedOptions={aggregatedOptions}
              onChange={onChangeFont}
              value={options?.style?.fontFamily ?? 'default'}
              backgroundColor={customSelectColors.grey}
            />
          </div>
        )}
      </div>
    </div>
  );

  return {
    toolbar,
    toolbarOptions
  };
};

export const onChangeSelection = (range, _source, quill, inlineSettings, setInlineSettings) => {
  const delta = quill.getContents(range.index, range.length || 1);
  if (delta.ops && delta.ops.length) {
    // Extract colour and make the toolbar picker the same colour
    const colorProps = delta.ops.find((option) => option.attributes && option.attributes.color);
    const boldProps = delta.ops.find((option) => option.attributes && option.attributes.bold);
    const italicProps = delta.ops.find((option) => option.attributes && option.attributes.italic);
    const underlineProps = delta.ops.find((option) => option.attributes && option.attributes.underline);
    const fontSizeProps = delta.ops.find((option) => option.attributes && option.attributes.size);
    const settings = {
      ...inlineSettings
    };

    settings.color = colorProps ? colorProps.attributes.color : false;
    settings.fontWeight = boldProps ? boldProps.attributes.bold : false;
    settings.fontStyle = italicProps ? italicProps.attributes.italic : false;
    settings.textDecoration = underlineProps ? underlineProps.attributes.underline : false;
    settings.fontSize = fontSizeProps ? fontSizeProps.attributes.size : false;

    setInlineSettings(settings);
  }
};

export const parseValue = (value) => {
  let getNodes = (str) => new DOMParser().parseFromString(str, 'text/html').body;
  let form = getNodes(value);
  const lastElement = form.children[form.children.length - 1];

  if (
    (lastElement.tagName === 'DIV' && lastElement.children.length === 1 && lastElement.children[0].tagName === 'BR') ||
    lastElement.tagName === 'BR'
  ) {
    lastElement.remove();
  }

  return form.innerHTML;
};
